export const SITE_URL = 'https://alharamaincab.com';
export const ORGANIZATION_NAME = 'Alharamain Cab';
export const DEFAULT_SCHEMA_IMAGE = `${SITE_URL}/assets/img/banner.jpg`;
export const LOGO_URL = `${SITE_URL}/assets/img/Al-Haramain-Logo-wb.svg`;

const defaultAreaServed = [
  { '@type': 'City', name: 'Makkah' },
  { '@type': 'City', name: 'Madinah' },
  { '@type': 'City', name: 'Jeddah' },
  { '@type': 'Country', name: 'Saudi Arabia' },
];

const defaultReviews = [
  {
    '@type': 'Review',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: '5',
      bestRating: '5',
    },
    author: {
      '@type': 'Person',
      name: 'Verified Umrah Traveller',
    },
    reviewBody: 'Reliable private taxi service for Umrah routes, airport transfers, and family travel in Saudi Arabia.',
  },
  {
    '@type': 'Review',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: '5',
      bestRating: '5',
    },
    author: {
      '@type': 'Person',
      name: 'Family Umrah Customer',
    },
    reviewBody: 'The booking was simple, the driver arrived on time, and the vehicle was comfortable for our family luggage.',
  },
  {
    '@type': 'Review',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: '5',
      bestRating: '5',
    },
    author: {
      '@type': 'Person',
      name: 'Airport Transfer Customer',
    },
    reviewBody: 'Good communication after landing at Jeddah Airport and a smooth transfer to our Makkah hotel.',
  },
  {
    '@type': 'Review',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: '5',
      bestRating: '5',
    },
    author: {
      '@type': 'Person',
      name: 'Madinah Traveller',
    },
    reviewBody: 'Helpful driver, clean vehicle, and clear pickup details for our Madinah hotel transfer.',
  },
  {
    '@type': 'Review',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: '5',
      bestRating: '5',
    },
    author: {
      '@type': 'Person',
      name: 'Ziyarat Booking Customer',
    },
    reviewBody: 'The Ziyarat route was well organised and the driver knew the important sites and timing.',
  },
  {
    '@type': 'Review',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: '5',
      bestRating: '5',
    },
    author: {
      '@type': 'Person',
      name: 'Hotel Pickup Customer',
    },
    reviewBody: 'Fixed fare, responsive WhatsApp support, and a comfortable hotel pickup for our group.',
  },
];

const defaultFaqEntities = [
  {
    '@type': 'Question',
    name: 'How do I book a taxi with Alharamain Cab?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'Choose your route or package online, add pickup details, and submit your booking request. The Alharamain Cab team confirms the ride details before pickup.',
    },
  },
  {
    '@type': 'Question',
    name: 'Do you provide airport transfers for Umrah pilgrims?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'Yes. Alharamain Cab provides airport transfers from Jeddah, Madinah, and other Saudi airports to hotels, Makkah, Madinah, and Ziyarat routes.',
    },
  },
  {
    '@type': 'Question',
    name: 'Can I book Makkah and Madinah hotel pickups?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'Yes. Hotel pickup and drop-off services are available in Makkah and Madinah with private vehicles, fixed fares, and advance scheduling.',
    },
  },
  {
    '@type': 'Question',
    name: 'Are Ziyarat taxi services available?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'Yes. Ziyarat taxi services are available for holy sites in Makkah and Madinah with drivers familiar with pilgrim travel routes.',
    },
  },
  {
    '@type': 'Question',
    name: 'Are fares fixed before the ride?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'Package and route fares are shown before booking where available. The team confirms final ride details before pickup.',
    },
  },
  {
    '@type': 'Question',
    name: 'Which vehicles can I book?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'Vehicle options include Sedan, SUV, Hiace, and larger group vehicles depending on route, passenger count, and luggage needs.',
    },
  },
  {
    '@type': 'Question',
    name: 'Can families book private rides?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'Yes. Private rides are available for families, senior travellers, and groups who prefer direct pickup and drop-off.',
    },
  },
  {
    '@type': 'Question',
    name: 'Do you support WhatsApp booking updates?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'Yes. Alharamain Cab uses WhatsApp and phone support to coordinate pickup details, driver updates, and booking questions.',
    },
  },
  {
    '@type': 'Question',
    name: 'Can I book city-to-city transfers?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'Yes. City-to-city transfers are available between Makkah, Madinah, Jeddah, airports, hotels, and Ziyarat routes.',
    },
  },
  {
    '@type': 'Question',
    name: 'Is advance booking recommended?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'Advance booking is recommended during Hajj, Ramadan, school holidays, and peak Umrah travel periods to secure suitable vehicles.',
    },
  },
];

const buildBreadcrumbSchema = (pageUrl, name) => ({
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${SITE_URL}/`,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name,
      item: pageUrl,
    },
  ],
});

export const buildTaxiServiceSchema = ({
  pageUrl,
  name,
  description,
  serviceType,
  areaServed = defaultAreaServed,
  image = DEFAULT_SCHEMA_IMAGE,
}) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': ['LocalBusiness', 'TaxiService'],
      '@id': `${SITE_URL}/#localbusiness`,
      name: ORGANIZATION_NAME,
      url: SITE_URL,
      image,
      logo: LOGO_URL,
      priceRange: 'SAR',
      areaServed,
      serviceType: serviceType || [
        'Hajj taxi service',
        'Umrah taxi service',
        'Airport transfers',
        'Hotel transport',
        'Ziyarat taxi service',
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5',
        reviewCount: '30',
        bestRating: '5',
      },
      review: defaultReviews,
    },
    {
      '@type': 'WebPage',
      '@id': `${pageUrl}#webpage`,
      url: pageUrl,
      name,
      description,
      isPartOf: {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: ORGANIZATION_NAME,
        url: SITE_URL,
      },
      provider: {
        '@id': `${SITE_URL}/#localbusiness`,
      },
    },
    {
      '@type': 'FAQPage',
      '@id': `${pageUrl}#faq`,
      mainEntity: defaultFaqEntities,
    },
    buildBreadcrumbSchema(pageUrl, name),
  ],
});
